import { useEffect, type RefObject } from "react";
import { usePaneStore } from "../state/store-context";

/**
 * E-072 — the keying-station focus contract, in one place.
 *
 * A field takes DOM focus with its whole value SELECTED whenever it becomes
 * the active field (Tab/Enter advance, smart-skip, initial focus) *or*
 * whenever something explicitly asks for it via `requestFieldFocus` — the
 * error-stop case, which `isActive` alone cannot express. When the submit gate
 * refuses on the field the operator is already sitting on, `activeFieldIndex`
 * doesn't change, so an `isActive`-only effect never re-runs and the operator
 * is left with no caret (the accept gesture took focus) and no selection.
 *
 * Selecting rather than clearing is the point: the offending value stays
 * legible until the first replacing keystroke (Datacap parity).
 *
 * Subscribing to `req.index === fieldIndex` rather than the raw request means
 * a bump re-renders only the field it names, not every mounted field.
 *
 * `select()` is guarded because `<select>` elements don't have it (R-142) —
 * a dropdown just takes focus.
 */
export function useFieldFocusAndSelect(
  fieldIndex: number,
  isActive: boolean,
  inputRef: RefObject<HTMLInputElement | HTMLSelectElement | null>,
): void {
  const focusNonce = usePaneStore((s) =>
    s.fieldFocusRequest?.index === fieldIndex ? s.fieldFocusRequest.nonce : 0,
  );

  useEffect(() => {
    const el = inputRef.current;
    if (!isActive || !el) return;
    el.focus();
    if (el instanceof HTMLInputElement) el.select();
    // inputRef is a ref — stable by construction, deliberately not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, focusNonce]);
}
